'use client';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

/**
 * NavigationMenuFocusManager
 *
 * A lightweight client component that closes navigation submenus when the pathname changes.
 * This fixes the issue where submenus remain open after navigation via keyboard.
 *
 * Place this component inside a provider (like CartProvider) that wraps the entire app.
 */
export function NavigationMenuFocusManager() {
  const pathname = usePathname();

  useEffect(() => {
    // Remove focus from any focused elements to close navigation submenus
    // This ensures submenus close when navigation occurs via keyboard
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.blur) {
      activeElement.blur();
    }

    // Additionally, remove focus from any navigation group elements
    // that might be maintaining the submenu open state
    const navigationGroups = document.querySelectorAll('.group');
    navigationGroups.forEach((group) => {
      const focusedChild = group.querySelector(':focus') as HTMLElement;
      if (focusedChild) {
        focusedChild.blur();
      }
    });
  }, [pathname]);

  // This component renders nothing - it only manages focus side effects
  return null;
}
